import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ValidationRulesFacade } from '../../facade/validation-rules.facade';
import { LocationHierarchyTabComponent } from '../location-hierarchy-tab/location-hierarchy-tab.component';
import { RuleTypesTabComponent } from '../rule-types-tab/rule-types-tab.component';
import { RulesByTemplateTabComponent } from '../rules-by-template-tab/rules-by-template-tab.component';

type TabId = 'rules' | 'location' | 'rule-types';

/**
 * Top-level page for the Validation Rules admin feature.
 *
 * Hosts two tabs:
 * - **Rules by Template**: template-centric CRUD over field + cross-field rules.
 * - **Location Hierarchy**: grouped view of the auto-generated
 *   CROSS_FIELD_VALUE_DEPENDENCY rules that enforce country -> state -> county.
 *
 * On init we kick off:
 * - `loadCatalog()` — the rule-types catalog is needed by both tabs (e.g. to
 *   display rule type labels). Cached on the service after the first fetch.
 * - `loadFieldRules()` + `loadCrossFieldRules()` — populates the store so
 *   either tab renders immediately on switch.
 */
@Component({
  selector: 'app-validation-rules-page',
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    RulesByTemplateTabComponent,
    LocationHierarchyTabComponent,
    RuleTypesTabComponent,
  ],
  templateUrl: './validation-rules-page.component.html',
})
export class ValidationRulesPageComponent implements OnInit {
  private facade = inject(ValidationRulesFacade);

  activeTab = signal<TabId>('rules');

  ngOnInit(): void {
    this.facade.loadCatalog();
    this.facade.loadFieldRules();
    this.facade.loadCrossFieldRules();
  }

  setTab(tab: TabId): void {
    this.activeTab.set(tab);
  }
}
